"use client";

import AddShoppingCart from "@mui/icons-material/AddShoppingCart";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Close from "@mui/icons-material/Close";
import Star from "@mui/icons-material/Star";
import StarBorder from "@mui/icons-material/StarBorder";
import StarHalf from "@mui/icons-material/StarHalf";
import React, { useMemo, useState } from "react";
import SalifyFallback from "src/lib/components/salify-fallback/default";
import SalifyLink from "src/lib/components/link/salify-link";
import LazyImage from "src/lib/components/salify-next-layout/default/components/LazyImage";
import useCart from "src/lib/components/salify-next-layout/default/hooks/useCart";
import { Category, Product } from "src/lib/fetcher/client";
import { toAssetImages } from "src/lib/utils";

export interface ProductListSectionLuxeItem {
  id?: string;
  name: string;
  image: string;
  badge: string;
  badgeTone?: "primary" | "secondary";
  stock: string;
  delivery: string;
  price: string;
  compareAt?: string;
  ctaPrimary?: string;
  ctaSecondary?: string;
  accent?: "primary" | "secondary";
  /** Optional: product detail URL to navigate on card click. */
  href?: string;
  /** Optional: original product for commerce actions. */
  product?: Product;
}

export interface ProductListSectionLuxeFilter {
  label: string;
  active?: boolean;
  href?: string;
}

export interface ProductListSectionLuxeViewProps {
  sectionId?: string;
  title?: string;
  subtitle?: string;
  /** Same as `product-list-section/default` SectionCreator `seeMoreLink` (e.g. `/collection`). */
  seeMoreLink?: string;
  filters?: ProductListSectionLuxeFilter[];
  products?: ProductListSectionLuxeItem[];
  /** Primary CTA handler (e.g. add-to-cart). */
  onAddToCart?: (item: ProductListSectionLuxeItem) => void;
  /** With `onFilterChange`, chips are controlled (static CMS only). */
  filterActiveIndex?: number;
  onFilterChange?: (index: number) => void;
}

/** Presentational grid (Stitch / Cyber Sea style). */
export const ProductListSectionLuxeView: React.FC<ProductListSectionLuxeViewProps> = ({
  sectionId = "products",
  title = "",
  subtitle = "",
  seeMoreLink,
  filters = [],
  products = [],
  onAddToCart,
  filterActiveIndex: controlledFilterIndex,
  onFilterChange,
}) => {
  const theme = useTheme();
  const isMobileDialog = useMediaQuery(theme.breakpoints.down("sm"));
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductListSectionLuxeItem | null>(null);
  const [selectedImg, setSelectedImg] = useState<string | null>(null);
  const [internalFilter, setInternalFilter] = useState(() => {
    const i = filters.findIndex((f) => f.active);
    return i >= 0 ? i : 0;
  });
  const isControlled = controlledFilterIndex !== undefined && onFilterChange !== undefined;
  const activeFilter = isControlled ? controlledFilterIndex! : internalFilter;
  const setActiveFilter = (i: number) => {
    if (isControlled) onFilterChange!(i);
    else setInternalFilter(i);
  };
  const openDetail = (item: ProductListSectionLuxeItem) => {
    setSelectedProduct(item);
    setSelectedImg(item.image);
    setDetailOpen(true);
  };

  return (
    <Box id={sectionId} component="section" sx={{ py: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg" sx={{ px: { xs: 2, md: 3 } }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "flex-end" }}
          spacing={4}
          sx={{ mb: { xs: 4, md: 6 } }}
        >
          <Stack spacing={1}>
            <Typography
              component="h2"
              sx={{
                fontWeight: 900,
                letterSpacing: "-0.03em",
                fontSize: { xs: "2.5rem", md: "3.5rem" },
                color: "text.primary",
              }}
            >
              {title}
            </Typography>
            {subtitle ? (
              <Typography sx={{ color: "text.secondary", fontWeight: 500, fontSize: "1.1rem" }}>
                {subtitle}
              </Typography>
            ) : null}
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent={{ xs: "flex-start", md: "flex-end" }}
            flexWrap="wrap"
            sx={{ gap: 1 }}
          >
            {seeMoreLink ? (
              <SalifyLink href={seeMoreLink}>
                <Typography
                  component="span"
                  sx={{
                    color: "primary.main",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  <SalifyFallback.Label path="View All" default="Xem tất cả" />
                </Typography>
              </SalifyLink>
            ) : null}
            {filters.length > 0 ? (
              <Stack
                direction="row"
                spacing={0.5}
                sx={{
                  flexWrap: "wrap",
                  bgcolor: "#f1f5f9",
                  p: 0.75,
                  borderRadius: 999,
                  border: "1px solid #e2e8f0",
                }}
              >
                {filters.map((f, i) => (
                  <Chip
                    key={`${f.label}-${i}`}
                    label={f.label}
                    component={f.href ? (SalifyLink as any) : "div"}
                    href={f.href}
                    onClick={f.href ? undefined : () => setActiveFilter(i)}
                    sx={{
                      borderRadius: 999,
                      px: 1,
                      fontWeight: 700,
                      fontSize: "0.875rem",
                      cursor: "pointer",
                      bgcolor: i === activeFilter ? "primary.main" : "transparent",
                      color: i === activeFilter ? "#fff" : "text.secondary",
                      boxShadow:
                        i === activeFilter ? "0 6px 20px -4px rgba(0,234,211,0.35)" : "none",
                      "&:hover": {
                        bgcolor: i === activeFilter ? "primary.main" : "rgba(0,0,0,0.04)",
                      },
                    }}
                  />
                ))}
              </Stack>
            ) : null}
          </Stack>
        </Stack>

        <Grid container spacing={{ xs: 4, md: 5 }}>
          {products.map((p, idx) => {
            const accentColor =
              p.accent === "secondary" || p.badgeTone === "secondary"
                ? theme.palette.secondary.main
                : theme.palette.primary.main;
            return (
              <Grid
                item
                xs={12}
                md={6}
                lg={4}
                key={p.id ?? `${p.name}-${idx}`}
                sx={{ display: "flex" }}
              >
                <Stack
                  onClick={() => openDetail(p)}
                  sx={{
                    borderRadius: "24px",
                    overflow: "hidden",
                    border: `1px solid ${theme.palette.primary.main}33`,
                    bgcolor: "rgba(255,255,255,0.85)",
                    backdropFilter: "blur(16px)",
                    width: "100%",
                    height: "100%",
                    flex: 1,
                    flexDirection: "column",
                    cursor: "pointer",
                    transition: "transform 0.5s, box-shadow 0.5s",
                    "&:hover": {
                      transform: "translateY(-12px)",
                      boxShadow: "0 24px 48px -12px rgba(0, 234, 211, 0.15)",
                    },
                    "&:hover .luxe-product-img": {
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      aspectRatio: "4/5",
                      p: 3,
                      bgcolor: "#f8fafc",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        height: "100%",
                        borderRadius: 2,
                        overflow: "hidden",
                      }}
                    >
                      <LazyImage
                        className="luxe-product-img"
                        alt={p.name}
                        width={700}
                        height={700}
                        src={toAssetImages(p.image)}
                        sizes="(max-width: 900px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        sx={{ p: 0, height: "100%", width: "100%" }}
                        style={{
                          objectFit: "cover",
                          width: "100%",
                          height: "100%",
                          transition: "transform 0.7s",
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        position: "absolute",
                        top: 40,
                        right: 40,
                        bgcolor: accentColor,
                        color: "#fff",
                        borderRadius: 999,
                        px: 2.5,
                        py: 0.75,
                        fontSize: "10px",
                        fontWeight: 900,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        boxShadow: 3,
                      }}
                    >
                      {p.badge}
                    </Box>
                  </Box>

                  <Stack
                    spacing={2}
                    sx={{
                      p: 4,
                      pt: 0,
                      flex: 1,
                      minHeight: 0,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Stack
                      sx={{
                        pt: 3,
                        flex: "0 0 auto",
                        minHeight: { xs: "5.5rem", sm: "5.75rem" },
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "1.5rem",
                          lineHeight: 1.25,
                          letterSpacing: "-0.02em",
                          color: "text.primary",
                          minWidth: 0,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          wordBreak: "break-word",
                        }}
                      >
                        {p.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "10px",
                          fontWeight: 700,
                          color: "text.secondary",
                          textTransform: "uppercase",
                          letterSpacing: "0.12em",
                        }}
                      >
                        {p.stock}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "10px",
                          fontWeight: 700,
                          color: "primary.main",
                          textTransform: "uppercase",
                          letterSpacing: "0.12em",
                        }}
                      >
                        {p.delivery}
                      </Typography>
                    </Stack>

                    <Stack
                      direction="row"
                      alignItems="baseline"
                      gap={1.5}
                      sx={{ flex: "0 0 auto" }}
                    >
                      <Typography sx={{ fontSize: "1.75rem", fontWeight: 900, color: accentColor }}>
                        {p.price}
                      </Typography>
                      {p.compareAt ? (
                        <Typography
                          component="span"
                          sx={{
                            color: "text.disabled",
                            textDecoration: "line-through",
                            fontSize: "0.9rem",
                          }}
                        >
                          {p.compareAt}
                        </Typography>
                      ) : null}
                    </Stack>

                    <Box
                      sx={{
                        pt: 1,
                        mt: "auto",
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        flex: "0 0 auto",
                      }}
                    >
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onAddToCart?.(p);
                        }}
                        sx={{
                          py: 2,
                          borderRadius: 2,
                          fontWeight: 900,
                          textAlign: "center",
                          px: 0,
                          maxWidth: "100%",
                          bgcolor: accentColor,
                          color: "#fff",
                          boxShadow: `0 8px 24px -6px ${accentColor}55`,
                          "& .MuiButton-startIcon": { display: "none" },
                          "&:hover": { bgcolor: accentColor, transform: "scale(0.98)" },
                        }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          <AddShoppingCart sx={{ fontSize: 18 }} />
                          <span>{p.ctaPrimary ?? "THÊM"}</span>
                        </Box>
                      </Button>
                    </Box>
                  </Stack>
                </Stack>
              </Grid>
            );
          })}
        </Grid>
      </Container>
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobileDialog}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: "28px" },
            bgcolor: "rgba(255,255,255,0.92)",
            backdropFilter: "blur(18px)",
            m: { xs: 0, sm: 2 },
          },
        }}
      >
        <IconButton
          onClick={() => setDetailOpen(false)}
          sx={{ position: "absolute", top: 12, right: 12, zIndex: 2 }}
        >
          <Close />
        </IconButton>
        <DialogContent sx={{ p: { xs: 2, md: 4 }, pb: { xs: 12, md: 4 } }}>
          {selectedProduct ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Stack spacing={{ xs: 1.5, md: 2 }}>
                  <Box
                    sx={{
                      borderRadius: { xs: 2.5, md: 4 },
                      overflow: "hidden",
                      bgcolor: "#f8fafc",
                      border: "1px solid rgba(0,0,0,0.06)",
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        aspectRatio: { xs: "4/3", md: "1/1" },
                      }}
                    >
                      <LazyImage
                        src={toAssetImages(selectedImg ?? selectedProduct.image)}
                        alt={selectedProduct.name}
                        width={800}
                        height={800}
                        sizes="(max-width: 900px) 100vw, 50vw"
                        sx={{ p: 0, height: "100%", width: "100%" }}
                        style={{
                          objectFit: "cover",
                          display: "block",
                          width: "100%",
                          height: "100%",
                        }}
                      />
                    </Box>
                  </Box>

                  {(() => {
                    const gallery: string[] = [];
                    if (selectedProduct.image) gallery.push(selectedProduct.image);
                    const mediaImgs =
                      selectedProduct.product?.medias?.map((m) => m?.value).filter(Boolean) ?? [];
                    for (const v of mediaImgs) {
                      if (!gallery.includes(v)) gallery.push(v);
                    }
                    // Keep imageUrl as the representative cover; medias fill the gallery thumbnails.
                    while (gallery.length < 2 && selectedProduct.image) {
                      gallery.push(selectedProduct.image);
                    }

                    const activeImg = selectedImg ?? selectedProduct.image;
                    return (
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                          gap: 1.5,
                          width: "100%",
                          maxWidth: 380,
                          mx: "auto",
                        }}
                      >
                        {gallery.slice(0, 4).map((src, idx) => {
                          const active = activeImg === src;
                          return (
                            <Box
                              key={`${src}-${idx}`}
                              component="button"
                              type="button"
                              onClick={() => setSelectedImg(src)}
                              style={{
                                position: "relative",
                                width: "100%",
                                aspectRatio: "1 / 1",
                                borderRadius: 12,
                                overflow: "hidden",
                                border: active
                                  ? `2px solid ${theme.palette.primary.main}`
                                  : "2px solid transparent",
                                padding: 0,
                                background: "transparent",
                                cursor: "pointer",
                              }}
                            >
                              <LazyImage
                                src={toAssetImages(src)}
                                alt=""
                                width={200}
                                height={200}
                                sizes="120px"
                                sx={{ p: 0, height: "100%", width: "100%" }}
                                style={{
                                  objectFit: "cover",
                                  display: "block",
                                  width: "100%",
                                  height: "100%",
                                }}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  })()}
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={{ xs: 1.5, md: 2.5 }} sx={{ pt: { xs: 0.5, md: 2 } }}>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignSelf: "flex-start",
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 999,
                      bgcolor: "rgba(0,234,211,0.10)",
                      border: "1px solid rgba(0,234,211,0.20)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "10px",
                        letterSpacing: "0.18em",
                        fontWeight: 900,
                        color: "primary.main",
                      }}
                    >
                      {selectedProduct.badge?.toUpperCase()}
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      fontWeight: 900,
                      fontStyle: "italic",
                      fontSize: { xs: "1.7rem", md: "2.4rem" },
                      letterSpacing: "-0.02em",
                      color: "text.primary",
                      lineHeight: 1.2,
                    }}
                  >
                    {selectedProduct.name}
                  </Typography>

                  <Stack direction="row" alignItems="baseline" gap={1.5} flexWrap="wrap">
                    <Typography
                      sx={{
                        fontSize: { xs: "1.9rem", md: "2.2rem" },
                        fontWeight: 900,
                        color: "primary.main",
                        lineHeight: 1.1,
                      }}
                    >
                      {selectedProduct.price}
                    </Typography>
                    {selectedProduct.compareAt ? (
                      <Typography sx={{ color: "text.disabled", textDecoration: "line-through" }}>
                        {selectedProduct.compareAt}
                      </Typography>
                    ) : null}
                  </Stack>

                  {(() => {
                    const rating = selectedProduct.product?.finalRating ?? 0;
                    const totalRating = selectedProduct.product?.totalRating ?? 0;
                    const full = Math.floor(rating);
                    const half = rating - full >= 0.5 ? 1 : 0;
                    const empty = Math.max(0, 5 - full - half);
                    return (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Stack direction="row" spacing={0.25} sx={{ color: "primary.main" }}>
                          {Array.from({ length: full }).map((_, i) => (
                            <Star key={`f-${i}`} sx={{ fontSize: 16 }} />
                          ))}
                          {half ? <StarHalf sx={{ fontSize: 16 }} /> : null}
                          {Array.from({ length: empty }).map((_, i) => (
                            <StarBorder key={`e-${i}`} sx={{ fontSize: 16 }} />
                          ))}
                        </Stack>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: 800,
                            color: "text.secondary",
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                          }}
                        >
                          ({totalRating} đánh giá)
                        </Typography>
                      </Stack>
                    );
                  })()}

                  {selectedProduct.product?.description ? (
                    <Box
                      sx={{
                        color: "text.secondary",
                        fontWeight: 600,
                        lineHeight: 1.7,
                        fontSize: { xs: "0.95rem", md: "1rem" },
                        "& p": { margin: 0, "& + p": { mt: 1.5 } },
                      }}
                      dangerouslySetInnerHTML={{
                        __html: selectedProduct.product.description,
                      }}
                    />
                  ) : (
                    <Typography
                      sx={{
                        color: "text.secondary",
                        fontWeight: 600,
                        lineHeight: 1.7,
                        fontSize: { xs: "0.95rem", md: "1rem" },
                      }}
                    >
                      Hải sản được tuyển chọn từ những vùng biển sạch nhất, bảo quản giúp giữ nguyên
                      độ tươi ngon như vừa mới đánh bắt.
                    </Typography>
                  )}

                  <Typography
                    sx={{
                      color: "text.secondary",
                      fontWeight: 700,
                      fontSize: "12px",
                      wordBreak: "break-word",
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                    }}
                  >
                    {selectedProduct.product
                      ? formatProductQtyUnitLine(selectedProduct.product as ProductWithLuxeMeta)
                      : selectedProduct.stock}
                  </Typography>
                  <Typography
                    sx={{
                      color: "primary.main",
                      fontWeight: 700,
                      fontSize: "12px",
                      wordBreak: "break-word",
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                    }}
                  >
                    {selectedProduct.delivery}
                  </Typography>

                  <Box
                    sx={{
                      pt: 0.5,
                      position: { xs: "fixed", md: "static" },
                      bottom: { xs: 0, md: "auto" },
                      left: { xs: 0, md: "auto" },
                      right: { xs: 0, md: "auto" },
                      width: { xs: "100%", md: "auto" },
                      px: { xs: 2, md: 0 },
                      pb: { xs: 2, md: 0 },
                      bgcolor: { xs: "rgba(255,255,255,0.95)", md: "transparent" },
                      backdropFilter: { xs: "blur(8px)", md: "none" },
                      borderTop: { xs: "1px solid rgba(0,0,0,0.08)", md: "none" },
                      zIndex: 3,
                    }}
                  >
                    <Box sx={{ width: "100%", maxWidth: 560, mx: "auto" }}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => onAddToCart?.(selectedProduct)}
                        sx={{
                          py: 2.2,
                          borderRadius: 3,
                          fontWeight: 900,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                          bgcolor: "primary.main",
                          boxShadow: "0 18px 38px -18px rgba(0,234,211,0.55)",
                          "& .MuiButton-startIcon": { ml: 0, mr: 1 },
                          "&:hover": { bgcolor: "primary.main", transform: "scale(0.99)" },
                        }}
                        startIcon={<AddShoppingCart />}
                      >
                        THÊM VÀO GIỎ
                      </Button>
                    </Box>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

// --- API-backed branch: same data contract as `product-list-section/default` (UI differs only) ---

export type ProductWithLuxeMeta = Product & {
  categoryId?: string;
  badge?: string;
  badgeTone?: "primary" | "secondary";
  deliveryLine?: string;
  /** API: available qty (preferred over `total` for display). */
  totalAvailableQuantity?: number;
  /** Alternate API field name. */
  totalAvailableStock?: number;
  unit?: { id?: string; name?: string } | null;
};

function formatVnd(n: number) {
  return `${n.toLocaleString("vi-VN")}đ`;
}

/** One line: `Tồn: {qty} {unit}` from API. */
function formatProductQtyUnitLine(p: ProductWithLuxeMeta): string {
  const qty =
    p.totalAvailableQuantity ??
    p.totalAvailableStock ??
    (typeof p.total === "number" ? p.total : Number(p.total));
  const unitName =
    p.unit && typeof p.unit === "object" && p.unit.name != null && String(p.unit.name).trim() !== ""
      ? String(p.unit.name).trim()
      : "";
  if (qty != null && Number.isFinite(Number(qty))) {
    const n = Number(qty);
    const num = n.toLocaleString("vi-VN");
    return unitName ? `Tồn: ${num} ${unitName}` : `Tồn: ${num}`;
  }
  return "Tồn: —";
}

function productToLuxeItem(
  p: ProductWithLuxeMeta,
  visualIndex: number,
  categoryLabel?: string,
): ProductListSectionLuxeItem {
  const refPrice = p.originalSalePrice ?? p.basePrice;
  const showCompare = p.salePrice < refPrice && (p.saleOffPercent ?? 0) >= 10;
  const compareAt = showCompare ? formatVnd(refPrice) : undefined;
  const tone = p.badgeTone ?? (visualIndex % 2 === 0 ? "primary" : "secondary");
  return {
    id: p.id,
    name: p.name,
    image: p.imageUrl,
    badge: p.badge ?? categoryLabel ?? "Sản phẩm",
    badgeTone: tone,
    stock: formatProductQtyUnitLine(p),
    delivery: p.deliveryLine ?? "Giao: Liên hệ cửa hàng",
    price: formatVnd(p.salePrice),
    compareAt,
    accent: tone === "secondary" ? "secondary" : "primary",
    href: `/product/${p.slugName || p.id}`,
    product: p,
  };
}

export interface ProductListSectionLuxeProps {
  sectionId?: string;
  title?: string;
  /** Prefer `subtitle`; `subTitle` accepted for CMS / JSON. */
  subtitle?: string;
  subTitle?: string;
  /** Static CMS: when `products` has items, optional filter chips (not used by API mode). */
  filters?: ProductListSectionLuxeFilter[];
  products?: ProductListSectionLuxeItem[];
  /** Same as default: link next to title (`/collection`). Pass `""` to hide. */
  seeMoreLink?: string;
  /** Parity with default; luxe cards do not use background variant like ProductItemVertical. */
  featuredOption?: string;
  /** Same as `product-list-section/default` (frequently viewed vs full list). */
  isFrequentlyViewed?: boolean;
  /** Label for all-category chip in API mode. */
  allLabel?: string;
}

const ProductListSectionLuxeFromApi: React.FC<{
  sectionId: string;
  title: string;
  subtitle: string;
  seeMoreLink?: string;
  isFrequentlyViewed?: boolean;
  allLabel?: string;
}> = ({ sectionId, title, subtitle, seeMoreLink, isFrequentlyViewed, allLabel = "Tất cả" }) => {
  const cart = useCart();
  const state = cart?.state;
  const dispatch = cart?.dispatch;
  const output = isFrequentlyViewed
    ? "output:productsByFrequentlyViewed.data"
    : "output:products.data";
  const rawProducts = SalifyFallback.useSelect<ProductWithLuxeMeta[]>(output);
  const categories = SalifyFallback.useSelect<Category[]>("output:categories.data");
  const [activeFilter, setActiveFilter] = useState(0);

  const getProductCategoryId = (p: ProductWithLuxeMeta): string | undefined =>
    p.category?.id ?? p.category?.code ?? p.categoryId;
  const getProductCategoryName = (p: ProductWithLuxeMeta): string | undefined =>
    p.category?.name ?? (p as any)?.categoryName;
  const getCategoryLabelById = (id?: string): string | undefined => {
    if (!id) return undefined;
    const match = (categories ?? []).find((c) => c.id === id || c.code === id);
    return match?.name;
  };

  const filterChips = useMemo(() => {
    const list: Array<{ id: string; code?: string; label: string; matchers?: string[] }> = [
      { id: "__all__", code: undefined, label: allLabel, matchers: [] },
    ];
    const fromCategories = (categories ?? []).map((c) => ({
      id: c.id,
      code: c.code,
      label: c.name,
      matchers: [c.id, c.code, c.name].filter((v): v is string => !!v),
    }));

    if (fromCategories.length) return [...list, ...fromCategories];

    // Fallback: derive labels directly from product payload.
    const products = rawProducts ?? [];
    const seen = new Set<string>();
    const derived = products
      .map((p) => {
        const key = getProductCategoryId(p);
        if (!key || seen.has(key)) return null;
        seen.add(key);
        const name = getProductCategoryName(p);
        return {
          id: key,
          code: key,
          label: name || key,
          matchers: [key, name].filter((v): v is string => !!v),
        };
      })
      .filter(Boolean) as Array<{ id: string; code?: string; label: string; matchers: string[] }>;

    return [...list, ...derived];
  }, [allLabel, categories, rawProducts]);

  const productsByFilter = useMemo(() => {
    const list = rawProducts ?? [];
    if (!list.length) return [];

    const hasCategoryId = list.some((p) => !!getProductCategoryId(p));
    const selected = filterChips[activeFilter];
    const selectedMatchers = selected?.matchers ?? [];

    if (!hasCategoryId || !selected || selected.id === "__all__") {
      return list;
    }
    return list.filter((p) => {
      const key = getProductCategoryId(p);
      const name = getProductCategoryName(p);
      return (
        (!!key && selectedMatchers.includes(key)) || (!!name && selectedMatchers.includes(name))
      );
    });
  }, [rawProducts, filterChips, activeFilter]);

  const mappedProducts = useMemo(() => {
    if (!productsByFilter.length) return [];
    return productsByFilter.map((p, i) => {
      const categoryId = getProductCategoryId(p);
      const categoryLabel = getProductCategoryName(p) ?? getCategoryLabelById(categoryId);
      return productToLuxeItem(p, i, categoryLabel);
    });
  }, [productsByFilter, categories]);

  const handleAddToCart = React.useCallback(
    (item: ProductListSectionLuxeItem) => {
      if (!dispatch || !item.product) return;
      const exist = (state?.cart ?? []).find((x) => x?.product?.id === item.product?.id);
      const nextQty = (exist?.quantity ?? 0) + 1;
      dispatch({
        type: "CHANGE_CART_AMOUNT",
        payload: { product: item.product, quantity: nextQty },
      });
    },
    [dispatch, state?.cart],
  );

  if (!rawProducts?.length) {
    return null;
  }

  return (
    <ProductListSectionLuxeView
      sectionId={sectionId}
      title={title}
      subtitle={subtitle}
      seeMoreLink={seeMoreLink}
      filters={filterChips.map((c, i) => ({
        label: c.label,
        active: i === activeFilter,
        href: c.id === "__all__" ? "/collection" : `/collection/${c.code || c.id}`,
      }))}
      products={mappedProducts}
      onAddToCart={handleAddToCart}
      filterActiveIndex={activeFilter}
      onFilterChange={setActiveFilter}
    />
  );
};

const ProductListSectionLuxe: React.FC<ProductListSectionLuxeProps> = ({
  sectionId = "products",
  title = "",
  subtitle,
  subTitle,
  filters: staticFilters,
  products: staticProducts,
  // Stitch LUXE design has no "View all" link.
  seeMoreLink = "",
  isFrequentlyViewed,
  allLabel,
}) => {
  const resolvedSubtitle = subtitle ?? subTitle ?? "";
  const linkHref = seeMoreLink === "" ? undefined : seeMoreLink;

  if (staticProducts && staticProducts.length > 0) {
    return (
      <ProductListSectionLuxeView
        sectionId={sectionId}
        title={title}
        subtitle={resolvedSubtitle}
        seeMoreLink={linkHref}
        filters={staticFilters ?? []}
        products={staticProducts}
      />
    );
  }

  return (
    <ProductListSectionLuxeFromApi
      sectionId={sectionId}
      title={title}
      subtitle={resolvedSubtitle}
      seeMoreLink={linkHref}
      isFrequentlyViewed={isFrequentlyViewed}
      allLabel={allLabel}
    />
  );
};

export default ProductListSectionLuxe;
